/**
 * The decrypted-read cache, in IndexedDB — sealed under a key derived from the
 * dataKey, so a copy of this browser's profile is worth no more than the
 * ciphertext in the cloud.
 *
 * What it buys is the steady state the whole design leans on: a page load costs
 * one manifest probe rather than re-fetching and re-opening every blob. What it
 * must not buy is a second, weaker copy of the vault — plaintext at rest needs
 * no key at all to read, so an entry is sealed with the same AEAD and the same
 * AAD binding as the blob it came from. Without the dataKey every entry is
 * noise, which is the property that lets this outlive a session at all.
 *
 * Two stores, both keyed by something immutable:
 *  - `manifests` by `manifestVersion`, because a version is written once.
 *  - `files` by `blobId:version`, because a blob's content at a version
 *    never changes; a new content means a new version, so an entry is never
 *    stale, only unused.
 */

import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { utf8 } from './bytes';
import { blobAad, KEY_BYTES, open, seal } from './crypto';

const DB_NAME = 'pondering-vault';
/** v2 seals what v1 wrote in the clear. The upgrade DROPS both stores rather
 *  than migrating: re-fetching is cheap, and the plaintext v1 left on disk is
 *  exactly what this version exists to remove. */
// 3: the cached manifest shape changed with the v2 wire format (path-keyed
// `files` replacing blobId-keyed `paths`). An upgrade drops both stores, which
// is what retires entries a reader would now misparse as an empty vault.
// 4: a cached manifest carries the wire format it was read in. A tab on an older
// bundle would read `{ manifest, format }` as a bare manifest with no files, and
// its next edit would push a body holding only the file it wrote.
const DB_VERSION = 4;
const MANIFESTS = 'manifests';
const FILES = 'files';

/** Local-only, so it agrees with nothing on the wire and needs no vector. */
const CACHE_KEY_INFO = 'pondering.readCache.v1';

/** Long enough for a cold disk, short enough that nobody watches a spinner over
 *  a cache. Every path out of here answers; none of them waits. */
const OPEN_TIMEOUT_MS = 3000;

let dbPromise: Promise<IDBDatabase | null> | null = null;

/**
 * Null on anything that is not a working database, and NEVER a promise that
 * fails to settle — the one thing a cache must not do is hold up the load it
 * exists to speed up.
 *
 * `blocked` is the case that taught this: it fires INSTEAD of success while
 * another connection holds an older version open, and neither success nor error
 * ever follows unless that connection closes. A tab running a build from before
 * the version bump keeps its handle for as long as it is open, so a reader that
 * waits on `open` waits for someone to close a tab they do not know is the
 * problem.
 */
function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;

  const attempt = new Promise<IDBDatabase | null>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    const timer = setTimeout(() => resolve(null), OPEN_TIMEOUT_MS);
    const settle = (db: IDBDatabase | null) => {
      clearTimeout(timer);
      resolve(db);
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      for (const store of [MANIFESTS, FILES]) {
        if (db.objectStoreNames.contains(store)) db.deleteObjectStore(store);
        db.createObjectStore(store);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // The other half of the lesson: this connection must not become the one
      // that blocks the NEXT version. Closing on request costs a cache miss and
      // saves a page that cannot load.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      settle(db);
    };
    request.onerror = () => settle(null);
    request.onblocked = () => settle(null);
  });

  // A failed open is not remembered: another tab closing should be enough to fix
  // it, without a reload.
  dbPromise = attempt.then((db) => {
    if (!db) dbPromise = null;
    return db;
  });
  return dbPromise;
}

export interface ReadCache {
  getManifest(version: number): Promise<unknown | null>;
  putManifest(version: number, manifest: unknown): Promise<void>;
  getFile(blobId: string, version: number): Promise<string | null>;
  putFile(blobId: string, version: number, text: string): Promise<void>;
  clear(): Promise<void>;
}

/** Where sealed entries land. Injectable so the sealing can be tested against
 *  what actually reaches storage, on a platform with no IndexedDB. */
export interface ByteStore {
  get(store: string, key: IDBValidKey): Promise<unknown | null>;
  put(store: string, key: IDBValidKey, value: Uint8Array): Promise<void>;
  clear(): Promise<void>;
}

export const indexedDbStore: ByteStore = { get, put, clear: clearAll };

export function mapStore(): ByteStore {
  const cells = new Map<string, Uint8Array>();
  return {
    get: async (store, key) => cells.get(`${store}/${String(key)}`) ?? null,
    put: async (store, key, value) => void cells.set(`${store}/${String(key)}`, value),
    clear: async () => cells.clear(),
  };
}

function deriveCacheKey(dataKey: Uint8Array): Uint8Array {
  return hkdf(sha256, dataKey, new Uint8Array(32), utf8(CACHE_KEY_INFO), KEY_BYTES);
}

/** Manifest entries get the same treatment as blobs: bound to their version,
 *  so an older validly-sealed manifest cannot be replayed under a newer one. */
function manifestAad(version: number): Uint8Array {
  return utf8(`manifest:${version}`);
}

/**
 * Every failure degrades to a miss. A browser in private mode, a full quota, a
 * blocked upgrade or an entry sealed under a key this session does not hold
 * must cost speed, never correctness.
 */
export function sealedIndexedDbCache(dataKey: Uint8Array, store: ByteStore = indexedDbStore): ReadCache {
  const cacheKey = deriveCacheKey(dataKey);

  const read = async (name: string, key: IDBValidKey, aad: Uint8Array): Promise<string | null> => {
    const stored = await store.get(name, key);
    if (!(stored instanceof Uint8Array)) return null;
    const plain = open(cacheKey, aad, stored);
    return plain === null ? null : new TextDecoder().decode(plain);
  };

  const write = (name: string, key: IDBValidKey, aad: Uint8Array, text: string): Promise<void> =>
    store.put(name, key, seal(cacheKey, aad, utf8(text)));

  return {
    async getManifest(version) {
      const text = await read(MANIFESTS, version, manifestAad(version));
      if (text === null) return null;
      try {
        return JSON.parse(text) as unknown;
      } catch {
        return null;
      }
    },
    async putManifest(version, manifest) {
      await write(MANIFESTS, version, manifestAad(version), JSON.stringify(manifest));
    },
    async getFile(blobId, version) {
      return read(FILES, `${blobId}:${version}`, blobAad(blobId, version));
    },
    async putFile(blobId, version, text) {
      await write(FILES, `${blobId}:${version}`, blobAad(blobId, version), text);
    },
    clear: () => store.clear(),
  };
}

/** Drops every entry, for a browser that is being handed back or forgotten.
 *  Sealed entries are already unreadable without the key; this is for the
 *  case where the user asked for the disk space and the tidiness. */
export async function clearAll(): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    await Promise.all([clearStore(db, MANIFESTS), clearStore(db, FILES)]);
  } catch {
    /* nothing to clear is the same outcome as clearing */
  }
}

/** For tests and for a browser with no IndexedDB at all. */
export function memoryCache(): ReadCache {
  const manifests = new Map<number, unknown>();
  const files = new Map<string, string>();
  return {
    getManifest: async (v) => manifests.get(v) ?? null,
    putManifest: async (v, m) => void manifests.set(v, m),
    getFile: async (b, v) => files.get(`${b}:${v}`) ?? null,
    putFile: async (b, v, t) => void files.set(`${b}:${v}`, t),
    clear: async () => {
      manifests.clear();
      files.clear();
    },
  };
}

async function get(store: string, key: IDBValidKey): Promise<unknown | null> {
  try {
    const db = await openDb();
    if (!db) return null;
    return await new Promise((resolve) => {
      const request = db.transaction(store, 'readonly').objectStore(store).get(key);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function put(store: string, key: IDBValidKey, value: unknown): Promise<void> {
  try {
    const db = await openDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* a miss next time is the only consequence */
  }
}

function clearStore(db: IDBDatabase, store: string): Promise<void> {
  return new Promise((resolve) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}
